import {
	depseudoizeCustomCaptions,
	depseudoizeText,
	isPseudoLocaleActive,
	pseudoizeCustomCaptions,
} from "./localisationResources";

// Caption localisation at the storage layer. The me.customCaptions blob is read
// all over the codebase — sometimes via getLocalStorageData, but ~100 files parse
// sessionStorage/localStorage directly — so the storage objects themselves are
// the only reliable single point to localise captions.
//
// Two active modes, both keyed on "me":
//  - Pseudo (dev QA): getItem returns a COPY with captions pseudo-ized; setItem
//    reconciles back to the real English value so the store stays English.
//  - A real non-English UI locale: the login/refresh responses always carry
//    English captions, so setItem overlays the active locale's captions on every
//    write. That keeps the stored blob localised no matter which of the ~8 places
//    (login, token refresh, portal, service edits, …) rewrites it, and getItem is
//    a passthrough. Switching back to English clears the overlay.

const ME_KEY = "me";
const SHIM_FLAG = "__localeCaptionStorageShim";

// Set by i18n when a non-English UI locale is active: the customCaptions map for
// that locale (derived from its bundle), or null for English.
let activeLocaleCaptions = null;

export const setActiveLocaleCaptions = (captions) => {
	activeLocaleCaptions = captions;
};

const pseudoizeRawMe = (raw) => {
	try {
		const parsed = JSON.parse(raw);
		if (!parsed?.customCaptions) return raw;

		return JSON.stringify({
			...parsed,
			customCaptions: pseudoizeCustomCaptions(parsed.customCaptions),
		});
	} catch {
		return raw;
	}
};

// Reconciles a written me blob back to real captions. For each caption, if the
// incoming value is exactly what getItem would have shown for the current stored
// (real) value, the caller didn't change it — keep the real value. Any OTHER
// wrapped value has no stored original to fall back to (e.g. the Navbar copying
// the blob into a store that was empty), so it is inverse-mapped back to plain
// text — otherwise the wrapping would be baked in as the "real" caption and
// survive turning pseudo off. Unwrapped values are genuine edits, kept as-is.
const restoreRealCaptions = (incomingRaw, storedRaw) => {
	try {
		const incoming = JSON.parse(incomingRaw);
		if (!incoming?.customCaptions) return incomingRaw;

		const realCaptions =
			(storedRaw ? JSON.parse(storedRaw) : null)?.customCaptions ?? {};
		const shown = pseudoizeCustomCaptions(realCaptions);

		const reconciled = Object.fromEntries(
			Object.entries(incoming.customCaptions).map(([key, value]) => [
				key,
				value === shown[key] ? realCaptions[key] : depseudoizeText(value),
			])
		);

		return JSON.stringify({ ...incoming, customCaptions: reconciled });
	} catch {
		return incomingRaw;
	}
};

// Heals a me blob whose captions were wrapped while pseudo was active (sessions
// that predate the restoreRealCaptions inverse-mapping fix, or any path that
// bypassed the patched setItem). Called when the toggle switches pseudo OFF, so
// the reloaded app reads clean captions. Safe to run any time: unmarked values
// pass through depseudoizeText untouched.
export const scrubPseudoCaptionsFromStorage = () => {
	[window.sessionStorage, window.localStorage].forEach((store) => {
		try {
			const raw = store.getItem(ME_KEY);
			if (!raw) return;
			const me = JSON.parse(raw);
			if (!me?.customCaptions) return;
			me.customCaptions = depseudoizeCustomCaptions(me.customCaptions);
			store.setItem(ME_KEY, JSON.stringify(me));
		} catch {
			// malformed/absent me — nothing to heal
		}
	});
};

// Overlays the active locale's captions onto a me blob being written, so a
// fresh English payload from the server is stored already localised.
const overlayLocaleCaptions = (incomingRaw) => {
	try {
		const incoming = JSON.parse(incomingRaw);
		if (!incoming?.customCaptions) return incomingRaw;

		return JSON.stringify({
			...incoming,
			customCaptions: { ...incoming.customCaptions, ...activeLocaleCaptions },
		});
	} catch {
		return incomingRaw;
	}
};

const patchStoragePrototype = (proto) => {
	if (!proto || typeof proto.getItem !== "function" || proto[SHIM_FLAG]) {
		return;
	}

	const originalGetItem = proto.getItem;
	const originalSetItem = proto.setItem;

	proto.getItem = function (key) {
		const raw = originalGetItem.call(this, key);

		// The key check MUST come first: isPseudoLocaleActive() itself reads
		// another storage key through this patched getItem.
		if (key !== ME_KEY || raw == null || !isPseudoLocaleActive()) {
			return raw;
		}

		return pseudoizeRawMe(raw);
	};

	proto.setItem = function (key, value) {
		if (key === ME_KEY && typeof value === "string") {
			if (isPseudoLocaleActive()) {
				value = restoreRealCaptions(value, originalGetItem.call(this, ME_KEY));
			} else if (activeLocaleCaptions) {
				value = overlayLocaleCaptions(value);
			}
		}

		originalSetItem.call(this, key, value);
	};

	Object.defineProperty(proto, SHIM_FLAG, {
		value: true,
		enumerable: false,
	});
};

export const installPseudoStorageShim = () => {
	if (typeof window === "undefined") {
		return;
	}

	// Patch the prototype of whatever the live storage objects are: real
	// Storage in the browser, the MockStorage class under jest. Installed in all
	// environments — the transforms are no-ops unless pseudo or a non-English
	// locale is active.
	new Set([
		Object.getPrototypeOf(window.sessionStorage),
		Object.getPrototypeOf(window.localStorage),
	]).forEach(patchStoragePrototype);
};
